// Graph Builderタブのメインコンポーネント

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { TabState } from '../../../types/pane.types';
import { useGraphBuilder } from './hooks/useGraphBuilder';
import { ReportModal } from './ReportModal';
import { GraphBuilderHeader } from './GraphBuilderHeader';
import { GraphSettings } from './GraphSettings';
import { GraphPreview } from './GraphPreview';
import { graphRegistry } from './GraphRegistry';
import { applyFilterToDataset } from '../../utils/applyFilterToDataset';
import { ExportGraphModal, GraphExportFormat, GraphExportOptions } from '../ExportGraphModal';
import { findSvgElement, prepareSvgForExport } from '../../utils/graphExportUtils';
import { downloadSvgFile } from '../../utils/webFileDownload';
import { getAspectRatioValue, calculateSizeWithAspectRatio } from '../../utils/aspectRatioUtils';
import logger from '../../utils/logger';
import * as styles from '../../styles/components/graph-builder.css.ts';

interface GraphBuilderTabContentProps {
  tab: TabState;
}

export function GraphBuilderTabContent({ tab }: GraphBuilderTabContentProps) {
  // カスタムフックを使用して状態とロジックを取得
  const {
    state,
    datasets,
    selectedDataset,
    graphTypes,
    graphCallbacks,
    filterExpression,
    handleDatasetChange,
    handleGraphTypeChange,
    handleConfigChange,
    handleFilterChange,
    handleAspectRatioChange,
    createNewReport,
    addGraphToReport,
    project
  } = useGraphBuilder({ tab });

  // プロジェクトの状態を監視
  useEffect(() => {
    logger.info('[GraphBuilder] Project state changed:', {
      hasProject: !!project,
      projectId: project?.id,
      hasReports: !!project?.reports,
      reportsCount: project?.reports?.length || 0
    });
  }, [project]);
  
  // レポート選択の状態
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [reportModalMode, setReportModalMode] = useState<'select' | 'create'>('select');

  // SVGエクスポートの状態
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPreviewSize, setExportPreviewSize] = useState<{ width: number; height: number } | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const previewSectionRef = useRef<HTMLDivElement>(null);

  // アスペクト比に基づく計算済みサイズ
  const [calculatedSize, setCalculatedSize] = useState<{ width: number; height: number } | null>(null);

  // 親コンテナのサイズを監視してアスペクト比に基づくサイズを計算
  useEffect(() => {
    const ratio = getAspectRatioValue(state.aspectRatio || '16:9');
    if (ratio === null || !previewSectionRef.current) {
      setCalculatedSize(null);
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: containerWidth, height: containerHeight } = entry.contentRect;
        // パディングを考慮（previewSectionのpadding: 12px）
        const size = calculateSizeWithAspectRatio(containerWidth, containerHeight, ratio, 12);
        setCalculatedSize(size);
      }
    });

    resizeObserver.observe(previewSectionRef.current);
    return () => resizeObserver.disconnect();
  }, [state.aspectRatio]);

  // フィルタ式がある場合は、データセットをフィルタリング
  const filteredDataset = useMemo(() => {
    if (!selectedDataset || !filterExpression) {
      return selectedDataset;
    }

    try {
      return applyFilterToDataset(selectedDataset, filterExpression);
    } catch (error) {
      logger.warn('[GraphBuilderTabContent] Failed to apply filter', { error });
      return selectedDataset; // フィルタ失敗時は元データを使用
    }
  }, [selectedDataset, filterExpression]);

  // Add to Reportボタンの有効/無効を判定
  const isAddToReportDisabled = useMemo(() => {
    // 基本的な条件チェック
    if (!state.currentConfig || !state.selectedGraphType || !filteredDataset) {
      return true;
    }

    // グラフタイプの設定を取得
    const entry = graphRegistry.get(state.selectedGraphType);
    if (!entry) {
      return true;
    }

    // 設定の検証
    const error = state.validationError || entry.renderer.validate(state.currentConfig, filteredDataset);
    return !!error;
  }, [state.currentConfig, state.selectedGraphType, state.validationError, filteredDataset]);

  // レポートモーダルを表示
  const handleAddToReport = () => {
    if (!state.currentConfig || !state.selectedGraphType || !selectedDataset || state.validationError) {
      logger.warn('[GraphBuilder] Cannot add to report: invalid state');
      return;
    }

    logger.info('[GraphBuilder] handleAddToReport - project state:', {
      hasProject: !!project,
      projectId: project?.id,
      projectReports: project?.reports,
      reportsLength: project?.reports?.length
    });

    const reports = project?.reports || [];
    // レポートが存在する場合は選択モード、存在しない場合は作成モードで開始
    setReportModalMode(reports.length > 0 ? 'select' : 'create');
    setShowReportModal(true);
  };
  
  // レポートモーダルでの確定処理
  const handleReportModalConfirm = (data: { reportId?: string; newReportName?: string; newReportDescription?: string }) => {
    logger.info('[GraphBuilder] handleReportModalConfirm called', { data, hasProject: !!project });

    if (data.reportId) {
      // 既存レポートに追加
      addGraphToReport(data.reportId);
    } else if (data.newReportName) {
      // 新規レポートを作成してグラフを追加
      const newReport = createNewReport(data.newReportName, data.newReportDescription || '');
      addGraphToReport(newReport.id);
    }

    // モーダルを閉じてリセット
    setShowReportModal(false);
    setSelectedReportId(null);
  };

  // Export as SVGボタンのハンドラ
  const handleExportSvg = () => {
    if (!state.currentConfig || !state.selectedGraphType || !selectedDataset || state.validationError) {
      logger.warn('[GraphBuilder] Cannot export SVG: invalid state');
      return;
    }

    // プレビューのSVGサイズを取得
    if (previewContainerRef.current) {
      const svg = previewContainerRef.current.querySelector('svg');
      if (svg) {
        const rect = svg.getBoundingClientRect();
        setExportPreviewSize({ width: rect.width, height: rect.height });
      }
    }

    setShowExportModal(true);
  };

  // SVGエクスポート実行ハンドラ
  const handleExecuteSvgExport = useCallback(async (
    filePath: string,
    _format: GraphExportFormat,
    options: GraphExportOptions
  ) => {
    if (!previewContainerRef.current) {
      throw new Error('Preview container not found');
    }

    try {
      logger.info('[GraphBuilder] Exporting SVG:', {
        filePath,
        ...options
      });

      // プレビューコンテナからSVG要素を取得
      const svg = findSvgElement(previewContainerRef.current);
      if (!svg) {
        throw new Error('No SVG element found in preview');
      }

      // グラフタイトルを取得（Custom Graphの場合は設定からタイトルを取得）
      const graphTitle = state.selectedGraphType || 'Graph';

      // SVGコンテンツを準備
      const svgContent = prepareSvgForExport(svg, graphTitle, options);

      // Web版: ブラウザのダウンロード機能を使用
      const filename = filePath.split('/').pop() || 'graph.svg';
      downloadSvgFile(svgContent, filename);

      logger.info('[GraphBuilder] SVG exported successfully', {
        filename,
        ...options
      });
    } catch (error) {
      logger.error('[GraphBuilder] SVG export error:', error);
      throw error;
    }
  }, [state.selectedGraphType]);

  return (
    <div className={styles.container}>
      {/* ヘッダーコンポーネント - 固定位置 */}
      <GraphBuilderHeader
        datasets={datasets}
        filterExpression={filterExpression}
        selectedDataset={selectedDataset}
        onDatasetChange={handleDatasetChange}
        onFilterChange={handleFilterChange}
      />

      {/* 2カラムレイアウト: 設定パネル（左）とプレビュー（右） */}
      <div className={styles.scrollableContent}>
        <div className={styles.twoColumnLayout}>
          {/* 設定パネル（左） */}
          <div className={styles.settingsPane}>
            <GraphSettings
              graphType={state.selectedGraphType}
              config={state.currentConfig}
              dataset={selectedDataset}
              graphTypes={graphTypes}
              aspectRatio={state.aspectRatio}
              onGraphTypeChange={handleGraphTypeChange}
              onConfigChange={handleConfigChange}
              onAspectRatioChange={handleAspectRatioChange}
            />
          </div>

          {/* プレビュー領域（右） */}
          <div className={styles.previewPane}>
            <div ref={previewSectionRef} className={styles.previewSection}>
              <div
                ref={previewContainerRef}
                className={styles.previewContainer}
                style={(() => {
                  // calculatedSizeがある場合は明示的なサイズを使用
                  if (calculatedSize) {
                    return {
                      width: `${calculatedSize.width}px`,
                      height: `${calculatedSize.height}px`,
                      alignSelf: 'center',
                      margin: 'auto'
                    };
                  }
                  // autoの場合はコンテナに合わせる（デフォルト）
                  return undefined;
                })()}
                onWheel={(e) => {
                  // プレビューエリア内でのホイールイベントが親コンテナに伝播しないようにする
                  e.stopPropagation();
                }}
              >
                <GraphPreview
                  key={state.previewKey}
                  graphType={state.selectedGraphType}
                  config={state.currentConfig}
                  dataset={selectedDataset}
                  filterExpression={filterExpression}
                  callbacks={graphCallbacks}
                  validationError={state.validationError}
                />
              </div>
            </div>

            {/* ボタンエリア - ペインの右下に配置 */}
            <div className={styles.previewPaneFooter}>
              <button
                className={styles.button}
                onClick={handleExportSvg}
                disabled={isAddToReportDisabled}
                data-testid="export-svg-button"
              >
                Export as SVG
              </button>
              <button
                className={styles.buttonPrimary}
                onClick={handleAddToReport}
                disabled={isAddToReportDisabled}
              >
                Add to Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* レポートモーダルコンポーネント */}
      <ReportModal
        isOpen={showReportModal}
        reports={project?.reports || []}
        mode={reportModalMode}
        selectedReportId={selectedReportId}
        onModeChange={setReportModalMode}
        onReportSelect={setSelectedReportId}
        onConfirm={handleReportModalConfirm}
        onClose={() => setShowReportModal(false)}
      />

      {/* SVGエクスポートモーダル */}
      <ExportGraphModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExecuteSvgExport}
        graphTitle={state.selectedGraphType || 'Graph'}
        initialWidth={exportPreviewSize?.width}
        initialHeight={exportPreviewSize?.height}
      />
    </div>
  );
}